import { useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiMutate, useApi } from "../hooks/useApi";
import { initials } from "../lib/builder-helpers";
import { istEmpfaengerDauerhaftBlockiert, mailfehlerText } from "../lib/mailfehler-anzeige";
import { openPrintableDocumentPost } from "../lib/print";
import { vorgangPfad } from "../lib/pfade";
import { ErrorNote, Icon, Input, LoadingRows, Modal, Button, Checkbox, Textarea } from "./components";
import { anlassAnzeige, formatDate } from "./format";
import { Row } from "./Row";
import { UnzustellbarDialog } from "./UnzustellbarDialog";
import { alsUnzustellbar, type UnzustellbarInfo } from "../lib/unzustellbar";

/**
 * "Erinnerung senden" (09.09.2026, Antoine-Screenshot der Veranstaltungsseite): der rote Knopf
 * schickte bisher SOFORT, ohne dass der Betreiber sah, was rausgeht ("Ich will dann wieder das
 * Mailformular in der Mitte sehen und alles auswählen können"). Dieser Dialog ist bewusst KEIN
 * Klon von `AngebotSendenDialog.tsx` (der ist auf den Erstversand zugeschnitten: Varianten-
 * Auswahl/-Abwahl, KI-Anschreiben-Neugenerierung, Kostenübernahme-Anforderung — nichts davon
 * gilt fuer eine Erinnerung, die per Definition dieselbe bereits versendete Version unveraendert
 * wiederholt, s. Kommentar an `POST /offers/resend`). Uebernommen ist das INTERAKTIONS-MUSTER
 * desselben Dialogs 1:1: Empfaenger-Zeile, Betreff-Feld, Nachricht-Textarea, Vorschau-Knopf,
 * Abbrechen/Jetzt-senden-Footer, derselbe Erfolg/Fehlschlag-Bildschirm danach.
 *
 * Datengrundlage: `GET .../offers/resend-preview` liefert beim Oeffnen die ECHTEN Vorschlags-
 * werte (Betreff + Anschreiben aus dem eingefrorenen Versand-Snapshot, nicht KI-generiert) -
 * das ist bereits exakt das, was ein Klick auf "Jetzt senden" ohne jede Aenderung verschicken
 * wuerde. Editiert der Betreiber, sendet `POST .../offers/resend` diese editierten Werte zurueck.
 */

interface ResendPreviewData {
  subject: string;
  letterText: string;
  toAddr: string | null;
  customerName: string | null;
  companyEmail: string | null;
  occasion: string | null;
  eventDate: string | null;
  guestCount: number | null;
  version: number;
}

interface ResendResult {
  version: number;
  emailSent: boolean;
  emailError: string | null;
  subject: string;
}

export function ErinnerungSendenDialog({
  open,
  onClose,
  eventId,
  eventNumber,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string | undefined;
  eventNumber: string | null | undefined;
  /** Nach erfolgreichem Versand: eventQ/offersQ/activityQ neu laden (Elternseite, wie bisher
   *  in `sendReminder()`). */
  onSent: () => void;
}) {
  const previewQ = useApi<ResendPreviewData>(open && eventId ? `/api/events/${eventId}/offers/resend-preview` : null);

  const [phase, setPhase] = useState<"compose" | "sent" | "mail_failed">("compose");
  const [subject, setSubject] = useState("");
  const [letterText, setLetterText] = useState("");
  const [ccSelf, setCcSelf] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [mailError, setMailError] = useState<string | null>(null);
  const [result, setResult] = useState<ResendResult | null>(null);
  // Neuversand-Schutz (20.10.2026): 409 `adresse_unzustellbar` -> Dialog mit neuer Adresse.
  const [unzustellbar, setUnzustellbar] = useState<UnzustellbarInfo | null>(null);

  // Wie `AngebotSendenDialog`: bei jedem Oeffnen synchron zuruecksetzen (useLayoutEffect, kein
  // Frame lang der alte sent/mail_failed-Bildschirm sichtbar).
  useLayoutEffect(() => {
    if (open) {
      setPhase("compose");
      setSendError(null);
      setMailError(null);
      setResult(null);
    }
  }, [open]);

  // Sobald die echten Vorschlagswerte da sind, Felder damit befuellen - nur beim ERSTEN Laden
  // nach dem Oeffnen (nicht bei jedem `previewQ.reload()`), sonst ueberschreibt ein spaeterer
  // Refetch eine bereits laufende Bearbeitung.
  const [prefilledFor, setPrefilledFor] = useState<string | null>(null);
  const prefillKey = eventId && previewQ.data ? `${eventId}:${previewQ.data.version}` : null;
  if (open && prefillKey && prefillKey !== prefilledFor) {
    setPrefilledFor(prefillKey);
    setSubject(previewQ.data!.subject);
    setLetterText(previewQ.data!.letterText);
  }
  if (!open && prefilledFor !== null) setPrefilledFor(null);

  // `useApi` reicht bei Fehlern nur `.message` durch (kein `statusCode`/`code`, s. `hooks/useApi.ts`
  // `ApiEnvelope`-Kommentar) - darum auf den Wortlaut pruefen, den die Route selbst festlegt
  // ("erneut zustellbare" fuer 409, "not_found" ohne eigenes `message`-Feld fuer 404).
  const notResendable = previewQ.error != null && (/erneut zustellbare/i.test(previewQ.error) || previewQ.error === "not_found");

  async function handlePreview() {
    if (!eventId) return;
    try {
      await openPrintableDocumentPost(`/api/events/${eventId}/offers/resend-preview`, { subject, letterText });
    } catch (err) {
      setSendError(err instanceof Error ? err.message : String(err));
    }
  }

  /** Der Versand selbst; wirft bei Fehlern (auch bei 409 `adresse_unzustellbar`). */
  async function sendeRequest(optionen: { trotzdem?: boolean }) {
    if (!eventId) return;
    const r = await apiMutate<ResendResult>(`/api/events/${eventId}/offers/resend`, "POST", {
      subject,
      letterText,
      ccSelf,
      ...(optionen.trotzdem ? { trotzdem: true } : {}),
    });
    setResult(r);
    if (r.emailSent) {
      setPhase("sent");
      onSent();
    } else {
      setMailError(r.emailError);
      setPhase("mail_failed");
    }
  }

  async function handleSend() {
    if (!eventId) return;
    setSending(true);
    setSendError(null);
    try {
      await sendeRequest({});
    } catch (err) {
      const u = alsUnzustellbar(err);
      if (u) setUnzustellbar(u);
      else setSendError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }

  const empfaengerBlockiert = istEmpfaengerDauerhaftBlockiert(mailError);

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      width={phase === "compose" ? "560px" : "468px"}
      title={
        previewQ.loading || previewQ.error
          ? "Erinnerung senden"
          : notResendable
            ? "Keine Erinnerung möglich"
            : phase === "compose"
              ? `Erinnerung an ${previewQ.data?.customerName ?? "Kunde"} senden`
              : phase === "mail_failed"
                ? "Erinnerung NICHT versendet"
                : "Erinnerung gesendet"
      }
      subtitle={
        !previewQ.loading && !previewQ.error && !notResendable && phase === "compose"
          ? [anlassAnzeige(previewQ.data?.occasion), previewQ.data?.eventDate ? formatDate(previewQ.data.eventDate) : null, previewQ.data?.guestCount ? `${previewQ.data.guestCount} Gäste` : null, eventNumber ? `Angebot ${eventNumber}` : null]
              .filter(Boolean)
              .join(" · ")
          : undefined
      }
      footer={
        previewQ.loading ? undefined : notResendable || previewQ.error ? (
          <Button type="button" onClick={onClose}>
            Schließen
          </Button>
        ) : phase === "compose" ? (
          <>
            <Button type="button" variant="secondary" className="mr-auto" onClick={() => void handlePreview()}>
              <Icon name="visibility" className="text-[16px]" /> Vorschau
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
              Abbrechen
            </Button>
            <Button type="button" data-testid="erinnerung-jetzt-senden" onClick={() => void handleSend()} disabled={sending || !subject.trim()}>
              <Icon name="send" className="text-[16px]" /> {sending ? "Sendet …" : "Jetzt senden"}
            </Button>
          </>
        ) : phase === "mail_failed" ? (
          <>
            <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
              Schließen
            </Button>
            {!empfaengerBlockiert && (
              <Button type="button" onClick={() => void handleSend()} disabled={sending}>
                <Icon name="send" className="text-[16px]" /> {sending ? "Sendet …" : "E-Mail erneut senden"}
              </Button>
            )}
          </>
        ) : (
          result && (
            <>
              {eventId && (
                <Link to={vorgangPfad(eventId)} className="mr-auto text-[12.5px] font-semibold text-secondary-fixed-dim hover:underline" onClick={onClose}>
                  Zum Vorgang
                </Link>
              )}
              <Button type="button" onClick={onClose}>
                Fertig
              </Button>
            </>
          )
        )
      }
    >
      {previewQ.loading ? (
        <LoadingRows rows={3} />
      ) : notResendable ? (
        <p className="text-[12.5px] text-secondary-fixed-dim">
          Der Vorgang hat gerade keine erneut zustellbare Angebotsversion (Status/Version geändert?).
        </p>
      ) : previewQ.error ? (
        <ErrorNote message={previewQ.error} />
      ) : phase === "compose" ? (
          <div className="space-y-3.5">
            {/* Empfänger */}
            <div>
              <label className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Empfänger</label>
              <div className="flex items-center gap-2.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest px-[11px] py-2 dark:border-outline/40">
                <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-primary/14 text-[11px] font-semibold text-primary">
                  {initials(previewQ.data?.customerName)}
                </span>
                <span className="truncate text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{previewQ.data?.customerName ?? "Kunde"}</span>
                <span className="truncate text-[12px] text-secondary-fixed-dim">{previewQ.data?.toAddr ?? "keine E-Mail hinterlegt"}</span>
              </div>
              {previewQ.data?.companyEmail && (
                <label className="mt-1.5 flex items-center gap-2 text-[12px] text-on-surface-variant">
                  <Checkbox checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} />
                  Kopie an mich senden ({previewQ.data.companyEmail})
                </label>
              )}
            </div>

            {/* Betreff */}
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Betreff</span>
              <Input
                data-testid="erinnerung-betreff"
                className="!py-2.5"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={300}
              />
            </label>

            {/* Nachricht */}
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Nachricht</span>
              <Textarea
                data-testid="erinnerung-nachricht"
                className="min-h-[160px] text-[12.5px] leading-relaxed"
                value={letterText}
                onChange={(e) => setLetterText(e.target.value)}
                maxLength={4000}
                placeholder="Sehr geehrte Damen und Herren, …"
              />
              <p className="mt-1 text-[10.5px] text-secondary-fixed-dim">
                Dieselbe Angebotsversion wie beim letzten Versand geht erneut raus — nur Betreff und Anschreiben lassen sich hier anpassen.
              </p>
            </label>

            {sendError && <ErrorNote message={sendError} />}
          </div>
      ) : phase === "mail_failed" ? (
        <div className="space-y-3">
          <p className="text-[12.5px] text-secondary-fixed-dim">
            {previewQ.data?.customerName ?? "Die Kundin"} hat <strong>keine E-Mail</strong> erhalten.
          </p>
          <p className="w-full rounded-lg bg-error/10 px-3 py-2 text-left text-[11px] font-mono text-error dark:text-[#e39b92]">
            {mailfehlerText(mailError)}
          </p>
        </div>
      ) : (
        result && (
          <div className="space-y-[9px]">
            <p className="pb-2 text-[12.5px] text-secondary-fixed-dim">
              {previewQ.data?.customerName ?? "Die Kundin"} wurde per E-Mail erinnert.
            </p>
            <Row label="Empfänger" value={previewQ.data?.toAddr ?? "–"} />
            <Row label="Betreff" value={result.subject} />
            <Row label="Version" value={String(result.version)} />
          </div>
        )
      )}
    </Modal>
    <UnzustellbarDialog
      info={unzustellbar}
      onClose={() => setUnzustellbar(null)}
      onSenden={async (optionen) => {
        await sendeRequest(optionen);
        setUnzustellbar(null);
      }}
    />
    </>
  );
}
